import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import TasksApiService from "@/core/services/api.service.tasks";
import TaskEditModal from "./TaskEditModal.vue";
import draggable from "vuedraggable";

export default {
  props: {},
  name: "tasks",
  components: {
    TaskEditModal,
    draggable
  },
  data() {
    return {
      loading: false,
      search: "",
      tasks: [],
      totalItems: 0,
      apiInitialized: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false
      },
      headers: [
        { text: "", value: "drag", width: "50px", sortable: false },
        { text: "Code", value: "code", width: "10%", sortable: false },
        { text: "Display Name", value: "displayName", width: "20%", sortable: false },
        { text: "Description", value: "description", width: "25%", sortable: false },
        { text: "Periodicity", value: "periodicity", width: "12%", sortable: false },
        { text: "Active", value: "isActive", width: "8%", align: "center", sortable: false },
        { text: "Email", value: "needToSendEmail", width: "8%", align: "center", sortable: false },
        { text: "Created", value: "createdAt", width: "12%", sortable: false },
        { text: "Actions", value: "actions", width: "10%", sortable: false, align: "center" }
      ],
      staticData: {
        taskPeriodicities: [],
        taskRewardTypes: [],
        nextCodePreview: ""
      },
      editDialog: false,
      deleteDialog: false,
      selectedTask: null
    };
  },
  watch: {
    options: {
      handler() {
        if (this.apiInitialized) {
          this.fetchTasks();
        }
      },
      deep: true
    },
    search() {
      this.debounceSearch();
    }
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Dashboard", route: "dashboard" },
      { title: "Tasks Management" }
    ]);

    try {
      await TasksApiService.init();
      this.apiInitialized = true;
      await this.loadStaticData();
      await this.fetchTasks();
    } catch (error) {
      console.error("Failed to initialize TasksApiService:", error);
      this.$bvToast?.toast("Failed to initialize API service", {
        variant: "danger",
        solid: true,
        autoHideDelay: 3000
      });
    }
  },
  methods: {
    async loadStaticData() {
      try {
        const response = await TasksApiService.getStaticData();
        this.staticData = response.data;
      } catch (error) {
        console.error("Failed to load static data:", error);
        this.$bvToast?.toast("Failed to load static data", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      }
    },

    async fetchTasks() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        const params = {
          page: page,
          maxCount: itemsPerPage,
          search: this.search || undefined,
          sortBy: sortBy.length > 0 ? sortBy[0] : undefined,
          sortDesc: sortDesc.length > 0 ? sortDesc[0] : undefined
        };

        const response = await TasksApiService.getTaskList(params);

        // Parse the response based on the actual API structure:
        // { metadata: { totalCount, remainingCount, hasMoreResults }, results: [...] }
        this.tasks = response.data.results || [];
        this.totalItems = response.data.metadata?.totalCount || 0;
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        this.$bvToast?.toast("Failed to load tasks", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
        this.tasks = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },

    async onDragEnd() {
      try {
        const priorities = {};
        this.tasks.forEach((task, index) => {
          priorities[task.code] = index + 1;
        });
        await TasksApiService.updatePriorities(priorities);
        this.$bvToast?.toast("Task order updated successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error("Failed to update priorities:", error);
        this.$bvToast?.toast("Failed to update task order", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
        await this.fetchTasks();
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        if (this.options.page !== 1) {
          this.options.page = 1;
        } else if (this.apiInitialized) {
          this.fetchTasks();
        }
      }, 500);
    },

    openCreateDialog() {
      this.selectedTask = null;
      this.editDialog = true;
    },

    async editTask(task) {
      try {
        this.loading = true;
        const response = await TasksApiService.getTaskById(task.id);
        this.selectedTask = response.data;
        this.editDialog = true;
      } catch (error) {
        console.error("Failed to fetch task details:", error);
        this.$bvToast?.toast("Failed to load task details", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    closeEditDialog() {
      this.editDialog = false;
      this.selectedTask = null;
    },

    async saveTask(taskData) {
      try {
        let response;

        if (taskData.id) {
          // Update existing task
          response = await TasksApiService.updateTask(taskData);
          this.$bvToast?.toast("Task updated successfully", {
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          });
        } else {
          // Create new task
          response = await TasksApiService.createTask(taskData);
          this.$bvToast?.toast("Task created successfully", {
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          });
        }

        this.closeEditDialog();
        await this.fetchTasks();
      } catch (error) {
        console.error("Failed to save task:", error);
        this.$bvToast?.toast("Failed to save task", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      }
    },

    confirmDelete(task) {
      this.selectedTask = task;
      this.deleteDialog = true;
    },

    async deleteTask() {
      if (!this.selectedTask) return;

      try {
        await TasksApiService.deleteTask(this.selectedTask.id);
        this.$bvToast?.toast("Task deleted successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
        this.deleteDialog = false;
        this.selectedTask = null;
        await this.fetchTasks();
      } catch (error) {
        console.error("Failed to delete task:", error);
        this.$bvToast?.toast("Failed to delete task", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      }
    },

    getPeriodicityColor(periodicity) {
      const colors = {
        Hourly: "red",
        Daily: "orange",
        Weekly: "blue",
        Monthly: "green",
        HalfYearly: "purple",
        Yearly: "indigo",
        OneTime: "grey"
      };
      return colors[periodicity] || "grey";
    },

    getRewardTypeColor(rewardType) {
      const colors = {
        OOBLEE_YELLOW: "yellow darken-2",
        OOBYTE: "blue",
        CASH_WITHDRAWBAL: "green",
        CASH_NON_WITHDRAWBAL: "orange"
      };
      return colors[rewardType] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch (error) {
        return dateString;
      }
    }
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
